// Pure helpers for the page preview timeline: which clip each dialogue plays,
// where it sits on the video timeline, and which stems make up the M&E bed.

import { getMusicEffectsStems } from "../../utils/versionMedia";

const dbToGain = (decibels) => {
  const value = Number(decibels);
  return decibels === null || decibels === undefined || !Number.isFinite(value)
    ? 1
    : Math.pow(10, value / 20);
};

// One clip per playable dialogue: the original audio when "Use original" is on
// or no translation audio exists, otherwise the selected translated audio.
export const buildPreviewClips = (dialogues = [], findTranslation) =>
  (Array.isArray(dialogues) ? dialogues : []).flatMap((dialogue, index) => {
    const start = Number(dialogue?.start);
    if (dialogue?.subtitleOnly === true || !Number.isFinite(start)) {
      return [];
    }

    const translation = findTranslation?.(dialogue?.translations);
    const useOriginal =
      Boolean(dialogue?.keepOriginalText) || !translation?.translatedAudio;
    const url = useOriginal
      ? dialogue?.originalAudio
      : translation.translatedAudio;

    if (!url) {
      return [];
    }

    // Signed URLs rotate; the object path keeps the decoded buffer cached.
    const objectPath = useOriginal
      ? dialogue?.originalAudioObjectName
      : translation?.translatedAudioPath;

    return [
      {
        index,
        start,
        url,
        cacheKey: objectPath || url,
        gain: useOriginal ? 1 : dbToGain(translation?.translatedAudioVolume),
      },
    ];
  });

// Changes only when what the timeline plays changes, not on every text edit or
// signed-URL refresh, so the preview is not rescheduled while typing.
export const getPreviewClipsSignature = (clips = []) =>
  clips
    .map((clip) => `${clip.index}:${clip.start}:${clip.cacheKey}:${clip.gain}`)
    .join("|");

// Where a clip starts relative to timeline time `time`: wait `delay` seconds,
// then play from `offset`. Null once the clip has finished.
export const getClipSchedule = (clipStart, clipDuration, time) => {
  const offset = Number(time) - Number(clipStart);

  if (!Number.isFinite(offset) || !(clipDuration > offset)) {
    return null;
  }

  return offset >= 0 ? { delay: 0, offset } : { delay: -offset, offset: 0 };
};

// Latest moment any decoded clip is still sounding, so the page preview does
// not cut off a clip that runs past its dialogue's end.
export const getTimelineEndTime = (clips = [], buffers) => {
  const endTimes = clips
    .map((clip) => {
      const duration = buffers?.get?.(clip.cacheKey)?.duration;
      return Number.isFinite(duration) ? clip.start + duration : null;
    })
    .filter((endTime) => endTime !== null);

  return endTimes.length > 0 ? Math.max(...endTimes) : null;
};

// The combined M&E stem when uploaded, otherwise music and effects together.
export const pickMusicEffectsStemReferences = (uploadedAudioTypes) =>
  getMusicEffectsStems(uploadedAudioTypes).map((stem) => stem.url);
